import { formatSiteDateTime } from '../domain/siteTime/siteTime';

export type OverviewV2CameraHealthRow = {
  cameraId: string;
  cameraLabel: string;
  location: string;
  status: string;
  lastFrame: string;
  measuring: string;
  freshness: string;
};

export type OverviewV2CameraHealthFacts = {
  cameraId: string;
  cameraLabel: string;
  location: string;
  cameraOnline: boolean | null;
  lastFrameAt: string | null;
  latestMeasurementEnd: string | null;
  measurementState: 'measuring' | 'not_measuring';
  freshnessState: 'fresh' | 'stale' | 'unavailable';
  freshnessAgeLabel: string | null;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function formatSiteClock(value: unknown, timeZone?: string | null): string | null {
  const raw = text(value);
  if (!raw) return null;
  return formatSiteDateTime(raw, timeZone) || raw;
}

function compactFreshnessAge(raw: string): string {
  return raw
    .replace(/^Freshness:\s*/i, '')
    .replace(/\s*[·•,]\s*stale window.*$/i, '')
    .replace(/\s+stale window.*$/i, '')
    .replace(/\bminutes old\b/i, 'min old')
    .replace(/\bminute old\b/i, 'min old')
    .trim();
}

export function buildOverviewV2CameraHealthFacts(args: {
  cameraId: string;
  cameraLabel: string;
  location: string;
  cameraOnline: boolean | null;
  lastFrameAt?: string | null;
  nowRows?: Array<{
    cameraId?: string | null;
    measurementValue?: number | null;
    currentThrough?: string | null;
    readStale?: boolean;
    ageLabel?: string | null;
    supportingActivityLabel?: string | null;
  }> | null;
  freshnessLabel?: string | null;
  ageLabel?: string | null;
}): OverviewV2CameraHealthFacts {
  const cameraId = text(args.cameraId);
  const allRows = args.nowRows || [];
  const mentionsCamera = allRows.some((row) => text(row.cameraId) === cameraId);
  const rows = mentionsCamera
    ? allRows.filter((row) => {
      const rowCam = text(row.cameraId);
      return !rowCam || rowCam === cameraId;
    })
    : allRows;
  const rowHasResult = (row: {
    measurementValue?: number | null;
    currentThrough?: string | null;
    supportingActivityLabel?: string | null;
  }) => (
    row.measurementValue != null
    || (Boolean(text(row.currentThrough)) && text(row.currentThrough) !== '—')
    || Boolean(text(row.supportingActivityLabel))
  );
  const latestMeasurementEnd = rows
    .map((row) => text(row.currentThrough))
    .find((value) => value && value !== '—') || null;
  const hasResult = rows.some(rowHasResult);
  const stale = rows.some((row) => row.readStale)
    || /stale|older than expected/i.test(`${args.freshnessLabel || ''} ${args.ageLabel || ''}`);
  const freshnessAgeLabel = compactFreshnessAge(
    text(args.freshnessLabel) || text(args.ageLabel) || rows.map((row) => text(row.ageLabel)).find(Boolean) || '',
  ) || null;
  return {
    cameraId: args.cameraId,
    cameraLabel: args.cameraLabel,
    location: args.location,
    cameraOnline: args.cameraOnline,
    lastFrameAt: args.lastFrameAt || null,
    latestMeasurementEnd,
    measurementState: hasResult ? 'measuring' : 'not_measuring',
    freshnessState: !hasResult ? 'unavailable' : (stale ? 'stale' : 'fresh'),
    freshnessAgeLabel,
  };
}

export function formatOverviewV2CameraMeasuring(
  facts: Pick<OverviewV2CameraHealthFacts, 'measurementState' | 'latestMeasurementEnd'>,
  timeZone?: string | null,
): string {
  if (facts.measurementState !== 'measuring') return 'Not currently measuring';
  const through = text(facts.latestMeasurementEnd);
  if (!through) return 'Measuring';
  const clock = formatSiteClock(through, timeZone);
  if (clock && /^measured through /i.test(through)) return through;
  return clock ? `Measured through ${clock}` : `Measured through ${through}`;
}

export function formatOverviewV2CameraFreshness(
  facts: Pick<OverviewV2CameraHealthFacts, 'measurementState' | 'freshnessState' | 'freshnessAgeLabel'>,
): string {
  const age = text(facts.freshnessAgeLabel);
  if (facts.freshnessState === 'stale') return age ? `${age} · Stale` : 'Stale';
  if (facts.freshnessState === 'fresh') return age || 'Fresh';
  return age || 'Unavailable';
}

export function formatOverviewV2CameraHealthRow(
  facts: OverviewV2CameraHealthFacts,
  timeZone?: string | null,
): OverviewV2CameraHealthRow {
  return {
    cameraId: facts.cameraId,
    cameraLabel: facts.cameraLabel,
    location: facts.location,
    status: cameraOnlineStatusLabel(facts.cameraOnline),
    lastFrame: facts.lastFrameAt
      ? (formatSiteDateTime(facts.lastFrameAt, timeZone, { includeYear: false }) || facts.lastFrameAt)
      : '',
    measuring: formatOverviewV2CameraMeasuring(facts, timeZone),
    freshness: formatOverviewV2CameraFreshness(facts),
  };
}

export function overviewV2HealthFreshnessCopy(args: {
  measuring: boolean;
  freshnessLabel?: string | null;
  ageLabel?: string | null;
  cameraFreshness?: string | null;
}): string {
  const facts = buildOverviewV2CameraHealthFacts({
    cameraId: 'site',
    cameraLabel: '',
    location: '',
    cameraOnline: null,
    nowRows: args.measuring ? [{ measurementValue: 1, readStale: /stale/i.test(`${args.freshnessLabel || ''} ${args.ageLabel || ''}`) }] : [],
    freshnessLabel: args.freshnessLabel,
    ageLabel: args.ageLabel || args.cameraFreshness,
  });
  return formatOverviewV2CameraFreshness(facts);
}

export function overviewV2CameraHealthValue(args: {
  availableCameraCount?: number | null;
  totalCameraCount?: number | null;
  onlineCount?: number | null;
  totalLinkedCameras?: number | null;
  cameraViewsLabel?: string | null;
}): string {
  if (typeof args.onlineCount === 'number' && typeof args.totalLinkedCameras === 'number' && args.totalLinkedCameras > 0) {
    return `${args.onlineCount} / ${args.totalLinkedCameras} cameras available`;
  }
  if (typeof args.availableCameraCount === 'number' && typeof args.totalCameraCount === 'number') {
    return `${args.availableCameraCount} / ${args.totalCameraCount} cameras available`;
  }
  const match = text(args.cameraViewsLabel).match(/(\d+)\s+of\s+(\d+)/i);
  if (match) return `${match[1]} / ${match[2]} cameras available`;
  return text(args.cameraViewsLabel) || 'Unavailable';
}

export function cameraLastFrameLabel(
  camera?: {
    health?: { last_frame?: string | null } | null;
    health_state?: { last_frame_at?: string | null; age_s?: number | null } | null;
  } | null,
  timeZone?: string | null,
): string {
  const raw = text(camera?.health?.last_frame) || text(camera?.health_state?.last_frame_at);
  if (!raw) return 'Unavailable';
  return formatSiteDateTime(raw, timeZone, { includeYear: false }) || raw;
}

export function cameraFreshnessLabel(camera?: {
  health_state?: { age_s?: number | null } | null;
} | null): string {
  const age = camera?.health_state?.age_s;
  if (typeof age !== 'number' || !Number.isFinite(age) || age < 0) return 'Unavailable';
  if (age < 60) return `${Math.round(age)}s`;
  const minutes = Math.round(age / 60);
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

export function cameraOnlineStatusLabel(online: boolean | null): string {
  if (online == null) return 'Unavailable';
  return online ? 'Online' : 'Offline';
}
